import { useEffect, useState } from "react";
import { useLang } from "../i18n";
import { Logo } from "./Logo";

// Flagship once-per-session intro: draws an Africa connectivity map on first load,
// then dissolves to reveal the site. Skippable + respects reduced motion.
const NODES = [
  { x: 300, y: 300, r: 9, hub: true, label: "KINSHASA · HUB", lx: 300, ly: 332, delay: 0.8 },
  { x: 190, y: 230, r: 5, label: "LAGOS", lx: 165, ly: 216, delay: 1.7 },
  { x: 352, y: 112, r: 5, label: "LE CAIRE", lx: 352, ly: 98, delay: 1.9 },
  { x: 416, y: 270, r: 5, label: "NAIROBI", lx: 438, ly: 266, delay: 2.1 },
  { x: 322, y: 470, r: 5, label: "JOHANNESBURG", lx: 322, ly: 492, delay: 2.3 },
];

const ROUTES = [
  "M300 300 Q 235 252 190 230",
  "M300 300 Q 342 195 352 112",
  "M300 300 Q 372 292 416 270",
  "M300 300 Q 302 398 322 470",
];

const INTL = [
  "M352 112 Q 240 40 110 34",
  "M416 270 Q 520 205 572 150",
  "M322 470 Q 450 512 574 486",
];

export const IntroMap = () => {
  const { lang } = useLang();
  // Lazy initializer: decide once at render so React 18 StrictMode's double-invoke
  // of effects can't skip the intro (previous bug: effect wrote sessionStorage flag,
  // then StrictMode's second effect run read the flag and jumped straight to "gone").
  const [phase, setPhase] = useState(() => {
    if (typeof window === "undefined") return "gone";
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const seen = window.sessionStorage && window.sessionStorage.getItem("bt_intro_seen");
    return (seen || reduce) ? "gone" : "in";
  });

  useEffect(() => {
    if (phase !== "in") return;
    document.body.style.overflow = "hidden";
    const tOut = setTimeout(() => setPhase("out"), 4300);
    const tGone = setTimeout(() => {
      try { sessionStorage.setItem("bt_intro_seen", "1"); } catch (e) {}
      setPhase("gone");
      document.body.style.overflow = "";
    }, 5100);
    return () => { clearTimeout(tOut); clearTimeout(tGone); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const skip = () => {
    try { sessionStorage.setItem("bt_intro_seen", "1"); } catch (e) {}
    setPhase("out");
    setTimeout(() => { setPhase("gone"); document.body.style.overflow = ""; }, 650);
  };

  if (phase === "gone" || phase === "init") return null;

  const tagline = lang === "en" ? "Connecting Africa." : "Connecter l'Afrique.";
  const sub = lang === "en" ? "Fibre · Satellite · Fixed wireless" : "Fibre · Satellite · Sans fil";

  return (
    <div
      data-testid="intro-map"
      onClick={skip}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#08090A] transition-opacity duration-700"
      style={{ opacity: phase === "out" ? 0 : 1, pointerEvents: phase === "out" ? "none" : "auto" }}
    >
      <style>{`
        @keyframes bt-draw { to { stroke-dashoffset: 0; } }
        @keyframes bt-pop { 0% { opacity: 0; transform: scale(0); } 60% { transform: scale(1.3); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes bt-fade-up { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes bt-spin { to { transform: rotate(360deg); } }
        .bt-path { stroke-dasharray: 1; stroke-dashoffset: 1; animation: bt-draw 1.3s ease forwards; }
        .bt-node { opacity: 0; transform-box: fill-box; transform-origin: center; animation: bt-pop 0.6s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .bt-label { opacity: 0; animation: bt-fade-up 0.7s ease forwards; }
        .bt-brand { opacity: 0; animation: bt-fade-up 0.9s ease forwards; animation-delay: 2.7s; }
      `}</style>

      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(#FF6600 1px, transparent 1px)", backgroundSize: "34px 34px" }} aria-hidden="true" />

      <svg viewBox="0 0 600 620" className="relative w-[min(88vw,560px)] h-auto" role="img" aria-label="Bula Telecoms — Africa connectivity">
        <defs>
          <linearGradient id="introRoute" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FF6600" />
            <stop offset="100%" stopColor="#FFC000" />
          </linearGradient>
          <radialGradient id="introGlow">
            <stop offset="0%" stopColor="#FF9900" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#FF6600" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Continent silhouette — drawn */}
        <path pathLength="1" className="bt-path"
          d="M300 40 C360 40 420 70 445 120 C470 165 480 200 460 250 C445 290 470 320 450 370 C430 425 390 470 340 500 C315 515 295 520 285 505 C270 480 250 450 225 420 C195 385 160 340 150 290 C140 240 150 190 175 150 C205 100 245 40 300 40 Z"
          fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.6"
          style={{ animationDelay: "0.1s", animationDuration: "1.5s" }} />

        {/* Radar rings on hub */}
        <g className="bt-node" style={{ animationDelay: "0.6s" }}>
          <circle cx="300" cy="300" r="44" fill="none" stroke="#FF6600" strokeOpacity="0.35" strokeWidth="1" strokeDasharray="4 5" style={{ transformBox: "fill-box", transformOrigin: "center", animation: "bt-spin 22s linear infinite" }} />
          <circle cx="300" cy="300" r="70" fill="none" stroke="#FF6600" strokeOpacity="0.18" strokeWidth="1" strokeDasharray="2 8" />
        </g>

        {/* Regional routes */}
        {ROUTES.map((d, i) => (
          <path key={i} pathLength="1" className="bt-path" d={d} fill="none" stroke="url(#introRoute)" strokeWidth="2" strokeLinecap="round"
            style={{ animationDelay: `${1.0 + i * 0.2}s`, animationDuration: "0.9s" }} />
        ))}

        {/* International arcs (leave the continent) */}
        {INTL.map((d, i) => (
          <path key={`i${i}`} pathLength="1" className="bt-path" d={d} fill="none" stroke="#FF9900" strokeOpacity="0.5" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="1"
            style={{ animationDelay: `${1.8 + i * 0.2}s`, animationDuration: "1s" }} />
        ))}

        {/* Satellite link */}
        <path pathLength="1" className="bt-path" d="M300 300 Q 250 150 332 34" fill="none" stroke="#FFC000" strokeOpacity="0.7" strokeWidth="1.4" strokeLinecap="round"
          style={{ animationDelay: "1.3s", animationDuration: "1s" }} />
        <circle className="bt-node" cx="332" cy="34" r="5" fill="#FFC000" style={{ animationDelay: "2.2s" }} />
        <text className="bt-label" x="332" y="22" textAnchor="middle" fill="#FFC000" fontSize="8" fontFamily="monospace" letterSpacing="2" style={{ animationDelay: "2.5s" }}>SATELLITE</text>

        {/* International endpoint dots */}
        {[[110, 34], [572, 150], [574, 486]].map(([cx, cy], i) => (
          <circle key={`e${i}`} className="bt-node" cx={cx} cy={cy} r="4" fill="#A8ADB3" style={{ animationDelay: `${2.4 + i * 0.15}s` }} />
        ))}

        {/* Nodes + labels */}
        {NODES.map((n, i) => (
          <g key={i}>
            <circle cx={n.x} cy={n.y} r={n.r * 3} fill="url(#introGlow)" className="bt-node" style={{ animationDelay: `${n.delay}s` }} />
            <circle className="bt-node" cx={n.x} cy={n.y} r={n.r} fill={n.hub ? "#FF6600" : "#FF9900"} style={{ animationDelay: `${n.delay}s` }} />
            <text className="bt-label" x={n.lx} y={n.ly} textAnchor="middle" fill={n.hub ? "#FFFFFF" : "#A8ADB3"} fontSize={n.hub ? 10 : 8} fontWeight={n.hub ? 800 : 400} fontFamily="monospace" letterSpacing="1.5" style={{ animationDelay: `${n.delay + 0.4}s` }}>{n.label}</text>
          </g>
        ))}
      </svg>

      {/* Brand + tagline */}
      <div className="bt-brand relative mt-4 flex flex-col items-center text-center px-6">
        <Logo />
        <h1 className="mt-5 text-2xl sm:text-3xl font-black tracking-tight text-white">{tagline}</h1>
        <p className="mt-2 text-xs font-mono tracking-[0.3em] uppercase text-[#FF9900]">{sub}</p>
      </div>

      <button onClick={(e) => { e.stopPropagation(); skip(); }} data-testid="intro-skip"
        className="absolute bottom-7 right-7 text-[11px] font-bold uppercase tracking-widest text-white/45 hover:text-white border border-white/15 hover:border-[#FF6600] rounded-full px-4 py-2 transition-colors">
        {lang === "en" ? "Skip" : "Passer"} ✕
      </button>
    </div>
  );
};
